import { Fragment, useState, useCallback } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Button,
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Breadcrumb from "../../common/breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import CommonLoader from "../../common/commonLoader";
import { useEffect, useRef } from "react";
import { ArrowBack } from "@mui/icons-material";
import {
  getVendorRegistrationById,
  saveOrUpdateVendorRegistration,
  checkVendorRegistrationStatutoryDuplicate,
  vendorRegistrationSendInvite,
  getAllPaymentTerms,
} from "../../api/ap_services";
import {
  Step1CompanyDetails,
  Step2BusinessDetails,
  Step3FactoryInfo,
  Step4Statutory,
  Step5OfficeUse,
  Step6Terms,
  Step7COBC,
  Step8Review,
} from "./steps";
import { useNavigate, useParams } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(3),
  },
  stepContent: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(3),
    paddingTop: theme.spacing(1), // Reduced top padding since stepper is sticky
  },
  card: {
    padding: theme.spacing(2),
  },
  stickyStepper: {
    position: "sticky",
    // top: 0,
    top: "5rem",
    zIndex: 5, //1000,
    backgroundColor: "#fff",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
}));

const STEP_LABELS_ALL = [
  "Company Details",
  "Factory Information",
  "Business Details",
  "Statutory Details",
  "Terms & Conditions",
  "COBC",
  "Review & Submit",
  "Office Use",
];
const STEP_LABELS_NO_OFFICE_USE = [
  "Company Details",
  "Factory Information",
  "Business Details",
  "Statutory Details",
  "Terms & Conditions",
  "COBC",
  "Review & Submit",
];

const LEVEL_APPROVAL_STATUS_OPTIONS = ["PENDING", "REJECTED", "APPROVED"];

const normalizeLevelApprovalStatus = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase();
  if (!normalized || normalized === "N") {
    return "PENDING";
  }
  if (LEVEL_APPROVAL_STATUS_OPTIONS.includes(normalized)) {
    return normalized;
  }
  return "PENDING";
};

/** Normalize API date to YYYY-MM-DD for date inputs */
const formatDateForInput = (val) => {
  if (val == null || val === "") return "";
  if (typeof val !== "string") return String(val);
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return val;
    return d.toISOString().slice(0, 10);
  } catch {
    return val;
  }
};

/**
 * Maps API response (getAllVendorEvaluation / getVendorRegistrationById) to form field names
 * so stored data populates the form for editing.
 */
const mapApiResponseToFormValues = (apiObj, defaults) => {
  const raw = Array.isArray(apiObj) ? apiObj[0] : apiObj;
  if (!raw || typeof raw !== "object") return defaults;
  const d = { ...defaults };

  const get = (key) => raw[key];
  const setForm = (formKey, value) => {
    if (value !== undefined && value !== null && value !== "")
      d[formKey] = value;
  };

  // Vendor Registration - Company Details tab
  setForm("companyName", get("companyName"));
  setForm("shareholdingStatus", get("shareholdingStatus"));
  setForm(
    "shareholdingStatusOtherRemarks",
    get("shareholdingStatusOtherRemarks"),
  );
  setForm("natureOfBusiness", get("natureOfBusiness"));
  // Company Address (multiline UI) - default line 1 from legacy officeAddress
  setForm("companyStreetDoorNo", get("companyStreetDoorNo"));
  setForm("companyAddressLine1", get("companyAddressLine1"));
  setForm("companyAddressLine2", get("companyAddressLine2"));
  setForm("companyAddressLine3", get("companyAddressLine3"));
  setForm("companyAddressLine4", get("companyAddressLine4"));
  setForm("companyAddressLine5", get("companyAddressLine5"));
  setForm("companyPostalCode", get("companyPostalCode"));
  setForm("companyCity", get("companyCity"));
  setForm("companyStateRegion", get("companyStateRegion"));
  setForm("companyCountry", get("companyCountry"));

  // Factory Address (multiline UI) - default line 1 from legacy factoryAddress
  setForm("factoryStreetDoorNo", get("factoryStreetDoorNo"));
  setForm("factoryAddressLine1", get("factoryAddress"));
  setForm("factoryAddressLine2", get("factoryAddressLine2"));
  setForm("factoryAddressLine3", get("factoryAddressLine3"));
  setForm("factoryAddressLine4", get("factoryAddressLine4"));
  setForm("factoryAddressLine5", get("factoryAddressLine5"));
  setForm("factoryPostalCode", get("factoryPostalCode"));
  setForm("factoryCity", get("factoryCity"));
  setForm("factoryStateRegion", get("factoryStateRegion"));
  setForm("factoryCountry", get("factoryCountry"));
  setForm("factoryAddressSameAsCompany", get("factoryAddressSameAsCompany"));
  setForm("yearOfEstablishment", get("yearOfEstablishment"));
  setForm("ownerName", get("ownerName"));
  setForm("ownerFathersName", get("ownerFathersName"));
  setForm("ownerContactNo", get("ownerContactNo"));
  setForm("ownerEmail", get("ownerEmail"));
  setForm("sendInvite", get("sendInvite"));
  setForm("partnerName", get("partnerName"));
  setForm("partnerContactNo", get("partnerContactNo"));
  setForm("partnerEmail", get("partnerEmail"));
  setForm("primaryContactName", get("primaryContactName"));
  setForm("primaryContactDesignation", get("primaryContactDesignation"));
  setForm("primaryContactNo", get("primaryContactNo"));
  setForm("primaryContactEmail", get("primaryContactEmail"));
  setForm("secondaryContactName", get("secondaryContactName"));
  setForm("secondaryContactDesignation", get("secondaryContactDesignation"));
  setForm("secondaryContactNo", get("secondaryContactNo"));
  setForm("secondaryContactEmail", get("secondaryContactEmail"));
  setForm("secondaryContactName2", get("secondaryContactName2"));
  setForm("secondaryContactDesignation2", get("secondaryContactDesignation2"));
  setForm("secondaryContactNo2", get("secondaryContactNo2"));
  setForm("secondaryContactEmail2", get("secondaryContactEmail2"));

  // Vendor Registration - Business Details tab
  setForm("turnoverApplicable", get("turnoverApplicable"));
  setForm("turnoverYear1", get("turnoverYear1"));
  setForm("turnoverRevenue1", get("turnoverRevenue1"));
  setForm("turnoverYear2", get("turnoverYear2"));
  setForm("turnoverRevenue2", get("turnoverRevenue2"));
  setForm("turnoverYear3", get("turnoverYear3"));
  setForm("turnoverRevenue3", get("turnoverRevenue3"));
  for (let i = 1; i <= 5; i++) {
    setForm(`natureProductRange${i}`, get(`natureProductRange${i}`));
    setForm(`natureBusinessShare${i}`, get(`natureBusinessShare${i}`));
    setForm(`top5Customer${i}`, get(`top5Customer${i}`));
  }
  // Vendor Registration - Factory Information tab
  setForm("factoryLicenceNo", get("factoryLicenceNo"));
  setForm("noOfPlants", get("noOfPlants"));
  setForm("totalAreaSqft", get("totalAreaSqft"));
  setForm(
    "totalProductionCapacityPerShift",
    get("totalProductionCapacityPerShift"),
  );
  setForm(
    "totalProductionCapacityPerMonth",
    get("totalProductionCapacityPerMonth"),
  );
  setForm("numberOfEmployeesLabours", get("numberOfEmployeesLabours"));
  setForm("ownRentalDetails", get("ownRentalDetails"));
  // Vendor Registration - Statutory tab
  setForm("itrAcknowledgementNo1", get("itrAcknowledgementNo1"));
  setForm("itrDateOfFiling1", formatDateForInput(get("itrDateOfFiling1")));
  setForm("itrAcknowledgementNo2", get("itrAcknowledgementNo2"));
  setForm("itrDateOfFiling2", formatDateForInput(get("itrDateOfFiling2")));
  setForm("gstApplicable", get("gstApplicable"));
  setForm("statutoryGstNo", get("statutoryGstNo"));
  setForm("statutoryPanNo", get("statutoryPanNo"));
  const normalizeYn = (v) => {
    if (v == null || v === "") return undefined;
    return String(v).trim().toUpperCase() === "Y" ? "Y" : "N";
  };
  const panYn = normalizeYn(
    get("isPanNoAlreadyExists") ?? get("is_pan_no_already_exists"),
  );
  const gstYn = normalizeYn(
    get("isGstNoAlreadyExists") ?? get("is_gst_no_already_exists"),
  );
  const accYn = normalizeYn(
    get("isAccNoAlreadyExists") ?? get("is_acc_no_already_exists"),
  );
  if (panYn !== undefined) d.isPanNoAlreadyExists = panYn;
  if (gstYn !== undefined) d.isGstNoAlreadyExists = gstYn;
  if (accYn !== undefined) d.isAccNoAlreadyExists = accYn;
  setForm("aadharLinkWithPan", get("aadharLinkWithPan"));
  setForm("msmeDeclaration", get("msmeDeclaration"));
  setForm("msmeType", get("msmeType"));
  setForm("msmeNo", get("msmeNo"));
  setForm("esiNo", get("esiNo"));
  setForm("pfNo", get("pfNo"));
  setForm("statutoryBankName", get("statutoryBankName"));
  setForm("branch", get("branch"));
  setForm("statutoryIfscCode", get("statutoryIfscCode"));
  setForm("statutoryAccountNumber", get("statutoryAccountNumber"));
  setForm("paymentTerms", get("paymentTerms"));
    setForm("msmeValidityFromDate", get("msmeValidityFromDate"));
  setForm("msmeValidityToDate", get("msmeValidityToDate"));

  setForm(
    "shareholdingStatusOtherRemarks",
    get("shareholdingStatusOtherRemarks"),
  );
  setForm("referenceFeedbackAboutVendor", get("referenceFeedbackAboutVendor"));
  setForm(
    "isExistingUser",
    get("isExistingUser") != null ? get("isExistingUser") : d.isExistingUser,
  );
  setForm("vendorAgreementStatus", get("vendorAgreementStatus"));
  setForm("vendorAgreementStatusFromDate", get("vendorAgreementStatusFromDate"));
  setForm("vendorAgreementStatusToDate", get("vendorAgreementStatusToDate"));
  setForm("securityChequeDetails", get("securityChequeDetails"));

  
  //Office use
  setForm("withholdingTaxType", get("withholdingTaxType"));
  setForm("withholdingTaxCode", get("withholdingTaxCode"));
  setForm("subject", get("subject"));
  setForm("qsrec", get("qsrec"));
  setForm("vendorCode", get("vendorCode"));
  setForm("vendorCategory", get("vendorCategory"));
  setForm("reconGroup", get("reconGroup"));
  setForm("subReconGroup", get("subReconGroup"));
  setForm("brand", get("brand"));
  setForm("approvalStatus", get("approvalStatus"));
  setForm("approvalRemarks", get("approvalRemarks"));
  d.firstLevelApprovalStatus = normalizeLevelApprovalStatus(
    get("firstLevelApprovalStatus") ?? get("first_level_approval_status"),
  );
  setForm("firstLevelApprovalBy", get("firstLevelApprovalBy"));
  setForm("firstLevelApprovalDate", get("firstLevelApprovalDate"));
  setForm("firstLevelApprovalRemarks", get("firstLevelApprovalRemarks"));
  d.secondLevelApprovalStatus = normalizeLevelApprovalStatus(
    get("secondLevelApprovalStatus") ?? get("second_level_approval_status"),
  );
  setForm("secondLevelApprovalBy", get("secondLevelApprovalBy"));
  setForm("secondLevelApprovalDate", get("secondLevelApprovalDate"));
  setForm("secondLevelApprovalRemarks", get("secondLevelApprovalRemarks"));
  d.thirdLevelApprovalStatus = normalizeLevelApprovalStatus(
    get("thirdLevelApprovalStatus") ?? get("third_level_approval_status"),
  );
  setForm("thirdLevelApprovalBy", get("thirdLevelApprovalBy"));
  setForm("thirdLevelApprovalDate", get("thirdLevelApprovalDate"));
  setForm("thirdLevelApprovalRemarks", get("thirdLevelApprovalRemarks"));
  d.sapTeamApprovalStatus = normalizeLevelApprovalStatus(
    get("sapTeamApprovalStatus") ?? get("sap_team_approval_status"),
  );
  setForm("sapTeamApprovalBy", get("sapTeamApprovalBy"));
  setForm("sapTeamApprovalDate", get("sapTeamApprovalDate"));
  setForm("sapTeamApprovalRemarks", get("sapTeamApprovalRemarks"));
  d.financeTeamApprovalStatus = normalizeLevelApprovalStatus(
    get("financeTeamApprovalStatus") ?? get("finance_team_approval_status"),
  );
  setForm("financeTeamApprovalBy", get("financeTeamApprovalBy"));
  setForm("financeTeamApprovalDate", get("financeTeamApprovalDate"));
  setForm("financeTeamApprovalRemarks", get("financeTeamApprovalRemarks"));


  setForm("agreeTerms", get("agreeTerms"));
  setForm("unitInspectedBy", get("unitInspectedBy"));
  setForm("isEdit", get("isEdit"));
  setForm("regNo", get("regNo"));
  setForm("preparedBy", get("preparedBy"));

  setForm("overallComments", get("overallComments"));

  const vendorTypeIdVal = get("vendorTypeId") ?? get("vendor_type_id");
  const vendorCodeVal = get("vendorTypeCode") ?? get("vendor_code");
  if (vendorTypeIdVal != null && vendorTypeIdVal !== "") {
    setForm("vendorTypeId", vendorTypeIdVal);
  }
  if (vendorCodeVal != null && vendorCodeVal !== "") {
    setForm("vendorTypeCode", vendorCodeVal);
  }
  if (vendorTypeIdVal || vendorCodeVal) {
    setForm("vendorCodeOption", {
      id: vendorTypeIdVal ?? undefined,
      code: vendorCodeVal ?? get("vendorTypeCode"),
      name: get("vendorName") ?? get("vendor_name") ?? "",
    });
  }
  setForm(
    "vendorTypeSubClassification",
    get("vendorTypeSubClassification") ?? get("vendor_type_sub_classification"),
  );
  setForm("termsAgreedDateTime", get("termsAgreedDateTime"));
  setForm("cobcAgreedDateTime", get("cobcAgreedDateTime"));
  setForm("termsAccepted", Boolean(get("termsAgreedDateTime")));
  setForm("cobcAccepted", Boolean(get("cobcAgreedDateTime")));


  return d;
};

const initialValues = {
  agreeTerms: false,
  overallComments: "",

  vendorTypeId: null,
  vendorTypeCode: "",
  vendorCodeOption: null,
  vendorTypeSubClassification: "",

  // Step 0 - Company Details tab
  companyName: "",
  shareholdingStatus: "",
  shareholdingStatusOtherRemarks: "",
  natureOfBusiness: "",
  // Company Address (multiline)
  companyStreetDoorNo: "",
  companyAddressLine1: "",
  companyAddressLine2: "",
  companyAddressLine3: "",
  companyAddressLine4: "",
  companyAddressLine5: "",
  companyPostalCode: "",
  companyCity: "",
  companyStateRegion: "",
  companyCountry: "INDIA",
  // Factory Address (multiline)
  factoryStreetDoorNo: "",
  factoryAddressLine1: "",
  factoryAddressLine2: "",
  factoryAddressLine3: "",
  factoryAddressLine4: "",
  factoryAddressLine5: "",
  factoryPostalCode: "",
  factoryCity: "",
  factoryStateRegion: "",
  factoryCountry: "INDIA",
  factoryAddressSameAsCompany: false,

  officeAddress: "",
  factoryAddress: "",
  yearOfEstablishment: "",
  ownerName: "",
  ownerFathersName: "",
  ownerContactNo: "",
  isOwnerContactNoAlreadyExists: "N",
  ownerEmail: "",
  sendInvite: false,
  partnerName: "",
  partnerContactNo: "",
  partnerEmail: "",
  primaryContactName: "",
  primaryContactDesignation: "",
  primaryContactNo: "",
  primaryContactEmail: "",
  secondaryContactName: "",
  secondaryContactDesignation: "",
  secondaryContactNo: "",
  secondaryContactEmail: "",
  secondaryContactName2: "",
  secondaryContactDesignation2: "",
  secondaryContactNo2: "",
  secondaryContactEmail2: "",

  // Step 1 - Business Details tab
  turnoverApplicable: "N",
  turnoverYear1: "",
  turnoverRevenue1: "",
  turnoverYear2: "",
  turnoverRevenue2: "",
  turnoverYear3: "",
  turnoverRevenue3: "",
  natureProductRange1: "",
  natureBusinessShare1: "",
  natureProductRange2: "",
  natureBusinessShare2: "",
  natureProductRange3: "",
  natureBusinessShare3: "",
  natureProductRange4: "",
  natureBusinessShare4: "",
  natureProductRange5: "",
  natureBusinessShare5: "",
  top5Customer1: "",
  top5Customer2: "",
  top5Customer3: "",
  top5Customer4: "",
  top5Customer5: "",

  // Step 2 - Factory Information tab
  factoryLicenceNo: "",
  noOfPlants: "",
  totalAreaSqft: "",
  totalProductionCapacityPerShift: "",
  totalProductionCapacityPerMonth: "",
  numberOfEmployeesLabours: "",
  ownRentalDetails: "",

  // Step 3 - Statutory Details tab
  itrAcknowledgementNo1: "",
  itrDateOfFiling1: "",
  itrAcknowledgementNo2: "",
  itrDateOfFiling2: "",
  gstApplicable: "N",
  statutoryGstNo: "",
  statutoryPanNo: "",
  isPanNoAlreadyExists: "N",
  isGstNoAlreadyExists: "N",
  isAccNoAlreadyExists: "N",
  aadharLinkWithPan: "",
  msmeDeclaration: "N",
  msmeType: "",
  msmeNo: "",
  esiNo: "",
  pfNo: "",
  statutoryBankName: "",
  branch: "",
  statutoryIfscCode: "",
  statutoryAccountNumber: "",
  paymentTerms: "",
  gstCertificateAttachment: null,
  gstSelfDeclarationAttachment: null,
  panScanAttachment: null,
  aadharPanLinkAttachment: null,
  aadharPanNoSignatureAttachment: null,
  msmeCertificateAttachment: null,
  msmeNoSignatureAttachment: null,
  bankDocumentAttachment: null,
  msmeValidityFromDate: "",
  msmeValidityToDate: "",
  vendorAgreementStatus: "",
  vendorAgreementStatusFromDate: "",
  vendorAgreementStatusToDate: "",
  securityChequeDetails: "",

  //office use
  withholdingTaxType: "",
  withholdingTaxCode: "",
  subject: "",
  qsrec: "",
  vendorCode: "",
  vendorCategory: "",
  reconGroup: "",
  subReconGroup: "",
  brand: "",
  approvalStatus: "",
  approvalRemarks: "",
  firstLevelApprovalStatus: "PENDING",
  firstLevelApprovalBy: "",
  firstLevelApprovalDate: "",
  firstLevelApprovalRemarks: "",
  secondLevelApprovalStatus: "PENDING",
  secondLevelApprovalBy: "",
  secondLevelApprovalDate: "",
  secondLevelApprovalRemarks: "",
  thirdLevelApprovalStatus: "PENDING",
  thirdLevelApprovalBy: "",
  thirdLevelApprovalDate: "",
  thirdLevelApprovalRemarks: "",
  sapTeamApprovalStatus: "PENDING",
  sapTeamApprovalBy: "",
  sapTeamApprovalDate: "",
  sapTeamApprovalRemarks: "",
  financeTeamApprovalStatus: "PENDING",
  financeTeamApprovalBy: "",
  financeTeamApprovalDate: "",
  financeTeamApprovalRemarks: "",

  termsOtp: "",
  termsAgreedDateTime: "",
  cobcOtp: "",
  cobcAgreedDateTime: "",
  termsAccepted: false,
  cobcAccepted: false,

  unitInspectedBy: "",
  isEdit: "N",
  regNo: "",
  preparedBy:"",
};

// Safe transform: avoid val.trim on non-string (prevents "trim is not a function")
const safeString = () =>
  Yup.string().transform((v) =>
    v != null && typeof v === "string" ? v.trim() : v == null ? "" : String(v),
  );

// MUI number inputs use "" when empty; Yup.number() rejects that unless transformed.
const numberFromInput = ({
  required = false,
  typeError = "Enter a valid number",
  min,
  minMessage,
  max,
  maxMessage,
  requiredMessage = "This field is required",
} = {}) => {
  let schema = Yup.number()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return required ? undefined : null;
      }
      return value;
    })
    .typeError(typeError);

  if (min != null) {
    schema = schema.min(min, minMessage ?? `Minimum is ${min}`);
  }
  if (max != null) {
    schema = schema.max(max, maxMessage ?? `Maximum is ${max}`);
  }

  return required
    ? schema.required(requiredMessage)
    : schema.nullable();
};

const hasAttachment = (value) => {
  if (value == null || value === "") return false;
  if (value instanceof File) return true;
  if (typeof value === "string" && value.trim() !== "") return true;
  if (typeof value === "object") {
    return Boolean(
      value.name || value.fileName || value.url || value.path || value.size,
    );
  }
  return false;
};

// const requiredAttachment = (message) =>
//   Yup.mixed().test("required-attachment", message, (value) =>
//     hasAttachment(value),
//   );

const requiredAttachment = (message) =>
  Yup.mixed()
    .nullable()
    .required(message)
    .test(
      "required-attachment",
      message,
      (value) => {
        return hasAttachment(value);
      }
    );

const validationSchema = Yup.object({
  // Step 0 - Company Details tab
  companyName: Yup.string().trim().required("Company Name is required"),
  // shareholdingStatus: Yup.array().when("sendInvite", {
  //   is: true,
  //   then: (schema) => schema.nullable(),
  //   otherwise: (schema) =>
  //     schema
  //       .min(1, "Select at least one Shareholding Status")
  //       .required("Shareholding Status is required"),
  // }),
//   shareholdingStatus: Yup.string().when("sendInvite", {
//   is: true,
//   then: (schema) => schema.nullable(),
//   otherwise: (schema) =>
//     schema.required("Shareholding Status is required"),
// }),
  shareholdingStatus: safeString().when("sendInvite", {
    is: (val) => val === true || val === "Y" || val === "true",
    then: (schema) => schema.nullable(),
    otherwise: (schema) =>
      schema.required("Shareholding Status is required"),
  }),
  // shareholdingStatusOtherRemarks: Yup.string().when("shareholdingStatus", {
  //   is: (val) => Array.isArray(val) && val.includes("Other"),
  //   then: (schema) =>
  //     schema.trim().required("Please provide description for Other"),
  //   otherwise: (schema) => schema.nullable(),
  // }),
  shareholdingStatusOtherRemarks: Yup.string().when(
    ["shareholdingStatus", "sendInvite"],
    {
      is: (shareholdingStatus, sendInvite) =>
        (sendInvite !== true && sendInvite !== "Y" && sendInvite !== "true") &&
        shareholdingStatus === "Other",
      then: (schema) =>
        schema
          .trim()
          .required("Please provide description for Other"),
      otherwise: (schema) => schema.nullable(),
    },
  ),
  // natureOfBusiness: safeString().when("sendInvite", {
  //   is: true,
  //   then: (schema) => schema.nullable(),
  //   otherwise: (schema) => schema.required("Nature of Business is required"),
  // }),
  // // Company Address validation
  // companyStreetDoorNo: safeString().when("sendInvite", {
  //   is: true,
  //   then: (schema) => schema.nullable(),
  //   otherwise: (schema) => schema.required("Street/Door No is required"),
  // }),
  // companyStreetDoorNo: safeString().when("sendInvite", {
  //   is: true,
  //   then: (schema) => schema.nullable(),
  //   otherwise: (schema) =>
  //     schema.required("Company Address Line 1 is required"),
  // }),

  natureOfBusiness: safeString()
  .required("Nature of Business is required"),

companyStreetDoorNo: safeString()
  .required("Street/Door No is required"),

companyAddressLine1: safeString()
  .required("Company Address Line 1 is required"),

  companyAddressLine2: safeString().nullable(),
  companyAddressLine3: safeString().nullable(),
  companyAddressLine4: safeString().nullable(),
  companyAddressLine5: safeString().nullable(),
  // companyPostalCode: safeString().nullable(),
  // companyCity: safeString().nullable(),
  // companyStateRegion: safeString().nullable(),
  // companyCountry: safeString().nullable(),

companyPostalCode: safeString()
  .matches(/^\d{6}$/, "Postal Code must be exactly 6 digits")
  .required("Postal Code is required"),

companyCity: safeString()
  .required("City is required"),

companyStateRegion: safeString()
  .required("State/Region is required"),

companyCountry: safeString()
  .required("Country is required"),

  // Factory Address validation (required only when not "Same as Company Address")
  factoryStreetDoorNo: safeString().when("factoryAddressSameAsCompany", {
    is: (val) => val === true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) =>
      schema.required("Factory Street/Door No is required"),
  }),
  factoryAddressLine1: safeString().when("factoryAddressSameAsCompany", {
    is: (val) => val === true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) =>
      schema.required("Factory Address Line 1 is required"),
  }),
  factoryAddressLine2: safeString().nullable(),
  factoryAddressLine3: safeString().nullable(),
  factoryAddressLine4: safeString().nullable(),
  factoryAddressLine5: safeString().nullable(),
  // factoryPostalCode: safeString().nullable(),
  // factoryCity: safeString().nullable(),
  // factoryStateRegion: safeString().nullable(),
  factoryPostalCode:safeString().when("factoryAddressSameAsCompany", {
    is: (val) => val === true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) =>
      schema.required("Postal Code is required")
    .matches(/^\d{6}$/, "Postal Code must be exactly 6 digits"),
  }),
    factoryCity:safeString().when("factoryAddressSameAsCompany", {
    is: (val) => val === true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) =>
      schema.required("City is required"),
  }),
      factoryStateRegion:safeString().when("factoryAddressSameAsCompany", {
    is: (val) => val === true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) =>
      schema.required("State/Region is required"),
  }),
  factoryCountry: safeString().nullable(),
  factoryAddressSameAsCompany: Yup.boolean(),
  // Legacy single-line fields
  officeAddress: safeString().nullable(),
  factoryAddress: safeString().nullable(),
  // yearOfEstablishment: safeString().when("sendInvite", {
  //   is: true,
  //   then: (schema) => schema.nullable(),
  //   otherwise: (schema) => schema.required("Year of Establishment is required"),
  // }),
  yearOfEstablishment: safeString()
  .required("Year of Establishment is required"),
  sendInvite: Yup.boolean(),
  ownerName: Yup.string().trim().required("Owner Name is required"),
  ownerFathersName: Yup.string().trim().nullable(),
  ownerContactNo: Yup.string()
    .trim()
    .matches(/^\d{10}$/, "Owner Contact Number must be exactly 10 digits")
    .required("Owner Contact Number is required"),
  ownerEmail: Yup.string()
    .trim()
    .email("Invalid email")
    .required("Owner E-mail ID is required"),
  partnerName: Yup.string().trim().nullable(),
  partnerContactNo: Yup.string().trim().nullable().matches(/^\d{10}$/, {
    message: "Owner Contact Number must be exactly 10 digits",
    excludeEmptyString: true,
  }),
  partnerEmail: Yup.string().trim().email("Invalid email").nullable(),
    primaryContactName: safeString()
  .required("Primary Contact Name is required"),
    primaryContactDesignation: safeString()
  .required("Primary Contact Designation is required"),
    primaryContactNo: safeString()
  .required("Primary Contact Number is required").matches(/^\d{10}$/, "Owner Contact Number must be exactly 10 digits"),
    primaryContactEmail: safeString()
  .required("Primary Contact E-mail ID is required").email("Invalid email"),
  secondaryContactName: Yup.string().trim().nullable(),
  secondaryContactDesignation: Yup.string().trim().nullable(),
  secondaryContactNo: Yup.string().trim().nullable().matches(/^\d{10}$/, {
    message: "Owner Contact Number must be exactly 10 digits",
    excludeEmptyString: true,
  }),
  secondaryContactEmail: Yup.string().trim().email("Invalid email").nullable(),
  secondaryContactName2: Yup.string().trim().nullable(),
  secondaryContactDesignation2: Yup.string().trim().nullable(),
  secondaryContactNo2: Yup.string().trim().nullable().matches(/^\d{10}$/, {
    message: "Owner Contact Number must be exactly 10 digits",
    excludeEmptyString: true,
  }),
  secondaryContactEmail2: Yup.string().trim().email("Invalid email").nullable(),

  // Step 1 - Business Details tab (optional)
  // turnoverYear1: Yup.string().nullable(),
  // turnoverRevenue1: Yup.string().nullable(),
  // turnoverYear2: Yup.string().nullable(),
  // turnoverRevenue2: Yup.string().nullable(),
  // turnoverYear3: Yup.string().nullable(),
  // turnoverRevenue3: Yup.string().nullable(),
  // // natureProductRange1: Yup.string().nullable(),
  // natureProductRange1: Yup.string()
  // .trim()
  // .required("At least one Product Range is required"),
  // natureBusinessShare1: Yup.string().nullable(),
  // natureProductRange2: Yup.string().nullable(),
  // natureBusinessShare2: Yup.string().nullable(),
  // natureProductRange3: Yup.string().nullable(),
  // natureBusinessShare3: Yup.string().nullable(),
  // natureProductRange4: Yup.string().nullable(),
  // natureBusinessShare4: Yup.string().nullable(),
  // natureProductRange5: Yup.string().nullable(),
  // natureBusinessShare5: Yup.string().nullable(),
  turnoverApplicable: Yup.string()
    .oneOf(["Y", "N"], "Turnover selection is required")
    .required("Turnover selection is required"),
/* turnoverYear1: Yup.mixed().when("turnoverApplicable", {
    is: "Y",
    then: () =>
      numberFromInput({
        required: true,
        typeError: "Enter valid year",
        requiredMessage: "Year is required",
      }),
    otherwise: () => numberFromInput({ typeError: "Enter valid year" }),
  }), */
  turnoverYear1: Yup.string()
  .trim()
  .matches(/^(FY\s)?[0-9]{4}(-[0-9]{2,4})?$/, {
    message: "Enter valid format like 2023-2024 or FY 2023-24",
    excludeEmptyString: true,
  })
  .max(12, "Year must not exceed 12 characters")
  .when("turnoverApplicable", {
    is: "Y",
    then: (schema) =>
      schema.required("Year is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  turnoverRevenue1: Yup.mixed().when("turnoverApplicable", {
    is: "Y",
    then: () =>
      numberFromInput({
        required: true,
        typeError: "Enter valid revenue",
        requiredMessage: "Revenue is required",
      }),
    otherwise: () => numberFromInput({ typeError: "Enter valid revenue" }),
  }),
  turnoverYear2: Yup.string()
  .nullable()
  .transform((value) => (value?.trim() === "" ? null : value))
  .matches(/^(FY\s)?[0-9]{4}(-[0-9]{2,4})?$/, {
    message: "Enter valid format like 2023-2024 or FY 2023-24",
    excludeEmptyString: true,
  })
  .max(12, "Year must not exceed 12 characters"),

  turnoverRevenue2: numberFromInput({
  typeError: "Enter valid percentage",
  min: 0,
  minMessage: "Minimum is 0%",
  max: 100,
  maxMessage: "Maximum is 100%",
}),
  turnoverYear3: Yup.string()
  .nullable()
  .transform((value) => (value?.trim() === "" ? null : value))
  .matches(/^(FY\s)?[0-9]{4}(-[0-9]{2,4})?$/, {
    message: "Enter valid format like 2023-2024 or FY 2023-24",
    excludeEmptyString: true,
  }),
   turnoverRevenue3: numberFromInput({
  typeError: "Enter valid percentage",
  min: 0,
  minMessage: "Minimum is 0%",
  max: 100,
  maxMessage: "Maximum is 100%",
}),

natureProductRange1: Yup.string()
  .trim()
  .required("At least one Product Range is required"),

natureBusinessShare1: numberFromInput({
  required: true,
  typeError: "Enter valid percentage",
  min: 0,
  minMessage: "Minimum is 0%",
  max: 100,
  maxMessage: "Maximum is 100%",
  requiredMessage: "Share percentage is required",
}),

natureProductRange2: Yup.string().nullable(),

natureBusinessShare2: numberFromInput({
  typeError: "Enter valid percentage",
  min: 0,
  minMessage: "Minimum is 0%",
  max: 100,
  maxMessage: "Maximum is 100%",
}),

natureProductRange3: Yup.string().nullable(),

natureBusinessShare3: numberFromInput({
  typeError: "Enter valid percentage",
  min: 0,
  minMessage: "Minimum is 0%",
  max: 100,
  maxMessage: "Maximum is 100%",
}),

natureProductRange4: Yup.string().nullable(),

natureBusinessShare4: numberFromInput({
  typeError: "Enter valid percentage",
  min: 0,
  minMessage: "Minimum is 0%",
  max: 100,
  maxMessage: "Maximum is 100%",
}),

natureProductRange5: Yup.string().nullable(),

natureBusinessShare5: numberFromInput({
  typeError: "Enter valid percentage",
  min: 0,
  minMessage: "Minimum is 0%",
  max: 100,
  maxMessage: "Maximum is 100%",
}),
  top5Customer1: Yup.string().trim().nullable(),
  top5Customer2: Yup.string().trim().nullable(),
  top5Customer3: Yup.string().trim().nullable(),
  top5Customer4: Yup.string().trim().nullable(),
  top5Customer5: Yup.string().trim().nullable(),

  // Step 2 - Factory Information tab (optional)
  factoryLicenceNo: Yup.string().trim().nullable(),
  noOfPlants: Yup.string().trim().nullable(),
  totalAreaSqft: Yup.string().trim().nullable(),
  totalProductionCapacityPerShift: Yup.string().trim().nullable(),
  totalProductionCapacityPerMonth: Yup.string().trim().nullable(),
  numberOfEmployeesLabours: Yup.string().trim().nullable(),
  ownRentalDetails: Yup.string().trim().nullable(),

  // Step 3 - Statutory Details tab (optional)
  itrAcknowledgementNo1: Yup.string()
    .trim()
    .nullable()
    .matches(/^[a-zA-Z0-9]{15}$/, {
      message:
        "Acknowledgement Number for ITR must be exactly 15 alphanumeric characters",
      excludeEmptyString: true,
    }),
  itrDateOfFiling1: Yup.string().nullable(),
  itrAcknowledgementNo2: Yup.string()
    .trim()
    .nullable()
    .matches(/^[a-zA-Z0-9]{15}$/, {
      message:
        "Acknowledgement Number for ITR must be exactly 15 alphanumeric characters",
      excludeEmptyString: true,
    }),
  itrDateOfFiling2: Yup.string().nullable(),
  gstApplicable: Yup.string()
    .oneOf(["Y", "N"], "GST selection is required")
    .required("GST selection is required"),
  statutoryGstNo: Yup.string()
    .trim()
    .when("gstApplicable", {
      is: "Y",
      then: (schema) =>
        schema
          .length(15, "GST Number must be exactly 15 characters")
          .required("GST Number is required")
          .test("pan-in-gst", "Invalid GST NO", function (value) {
            const gst = String(value || "").trim().toUpperCase();
            const pan = String(this.parent?.statutoryPanNo || "")
              .trim()
              .toUpperCase();
            if (!gst || !pan) return true;
            if (gst.length < 12 || pan.length !== 10) return true;
            return gst.substring(2, 12) === pan;
          }),
      otherwise: (schema) => schema.nullable(),
    }),
  statutoryPanNo: Yup.string()
    .trim()
    .required("PAN Number is required")
    .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN format. Must be 10 characters (e.g., ABCDE1234F)")
    .test(
      "pan-4th-letter-validation",
      function(value) {
        const shareholdingStatus = this.parent.shareholdingStatus;
        if (!value || value.length < 4) return true;
        
        const fourthLetter = value.charAt(3).toUpperCase();
        
        const validationMap = {
          "Private Limited": ["C"],
          "Partnership": ["F"],
          "Limited Liability": ["P", "C", "B"],
          "Public Listed": ["G"],
          "Proprietorship": ["P"],
          "Other": ["H", "A", "T", "B", "L", "J"]
        };
        
        const allowedLetters = validationMap[shareholdingStatus];
        if (!allowedLetters) return true;
        
        if (!allowedLetters.includes(fourthLetter)) {
          return this.createError({
            message: `PAN 4th letter must be ${allowedLetters.join(" or ")} for ${shareholdingStatus}`
          });
        }
        
        return true;
      }
    ),
  aadharLinkWithPan: Yup.string().oneOf(["Y", "N", ""]).nullable(),
  msmeDeclaration: Yup.string()
    .oneOf(["Y", "N"], "MSME declaration is required")
    .required("MSME declaration is required"),
  // msmeType: Yup.string().trim().nullable(),
  // msmeNo: Yup.string().trim().nullable(),
  msmeType: Yup.string()
  .trim()
  .when("msmeDeclaration", {
    is: "Y",
    then: (schema) => schema.required("MSME Type is required"),
    otherwise: (schema) => schema.nullable(),
  }),
msmeNo: Yup.string()
  .nullable()
  .when("msmeDeclaration", {
    is: "Y",
    then: (schema) =>
      schema
        .trim()
        .required("MSME No is required")
        .length(16, "MSME NO must be exactly 16 characters"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
/* msmeNo: Yup.string()
  .trim()
    .length(16, "MSME NO must be exactly 16 characters")
  .when("msmeDeclaration", {
    is: "Y",
    then: (schema) => schema.required("MSME No is required"),
    otherwise: (schema) => schema.nullable(),
  }), */
  esiNo: Yup.string().trim().nullable(),
  pfNo: Yup.string().trim().nullable(),
  // statutoryBankName: Yup.string().trim().nullable(),
  // branch: Yup.string().trim().nullable(),
  // statutoryIfscCode: Yup.string().trim().nullable(),
  // statutoryAccountNumber: Yup.string().trim().nullable(),
  statutoryBankName: Yup.string()
  .trim()
  .required("Bank Name is required"),

branch: Yup.string()
  .trim()
  .required("Branch is required"),

statutoryIfscCode: Yup.string()
  .trim()
  .uppercase()
  .length(11, "IFSC Code must be exactly 11 characters")
  .required("IFSC Code is required"),

statutoryAccountNumber: Yup.string()
  .trim()
  .matches(/^[0-9]+$/, "Account Number must contain only digits")
  .min(9, "Account Number must be at least 9 digits")
  .max(18, "Account Number must not exceed 18 digits")
  .required("Account Number is required"),

  paymentTerms: Yup.string().trim().nullable(),
  gstCertificateAttachment: Yup.mixed().when("gstApplicable", {
    is: "Y",
    then: () =>
      requiredAttachment("GST certificate attachment is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  gstSelfDeclarationAttachment: Yup.mixed().when("gstApplicable", {
    is: "N",
    then: () =>
      requiredAttachment("GST self declaration attachment is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  panScanAttachment: requiredAttachment("PAN scan attachment is required"),
  aadharPanLinkAttachment: Yup.mixed().nullable(),
  aadharPanNoSignatureAttachment: Yup.mixed().nullable(),
  msmeCertificateAttachment: Yup.mixed().when("msmeDeclaration", {
    is: "Y",
    then: () =>
      requiredAttachment("MSME certificate attachment is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  msmeNoSignatureAttachment: Yup.mixed().when("msmeDeclaration", {
    is: "N",
    then: () =>
      requiredAttachment("Signed MSME declaration attachment is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  bankDocumentAttachment: requiredAttachment(
    "Bank document attachment is required",
  ),
  msmeValidityFromDate: Yup.string()
    // MUI `type="date"` empty state is `""`. Treat it as missing.
    .transform((v, originalValue) =>
      originalValue === "" ? null : v,
    )
    .when("msmeDeclaration", {
      is: "Y",
      then: (schema) =>
        schema.required("MSME Validity From Date is required"),
      otherwise: (schema) => schema.nullable(),
    }),

  msmeValidityToDate: Yup.string()
    // MUI `type="date"` empty state is `""`. Treat it as missing.
    .transform((v, originalValue) =>
      originalValue === "" ? null : v,
    )
    .when("msmeDeclaration", {
      is: "Y",
      then: (schema) =>
        schema.required("MSME Validity To Date is required"),
      otherwise: (schema) => schema.nullable(),
    }),

  withholdingTaxType: Yup.mixed().nullable(),
  withholdingTaxCode: Yup.mixed().nullable(),
  subject: Yup.mixed().nullable(),
  qsrec: Yup.mixed().nullable(),
  vendorCode: Yup.mixed().nullable(),
  vendorCategory: Yup.mixed().nullable(),
  reconGroup: Yup.string().trim().nullable(),
  subReconGroup: Yup.string().trim().nullable(),
  brand: Yup.string().trim().nullable(),
  approvalStatus: Yup.mixed().nullable(),
  approvalRemarks: Yup.mixed().nullable(),
  firstLevelApprovalStatus: Yup.mixed().nullable(),
  firstLevelApprovalBy: Yup.mixed().nullable(),
  firstLevelApprovalDate: Yup.mixed().nullable(),
  firstLevelApprovalRemarks: Yup.mixed().nullable(),
  secondLevelApprovalStatus: Yup.mixed().nullable(),
  secondLevelApprovalBy: Yup.mixed().nullable(),
  secondLevelApprovalDate: Yup.mixed().nullable(),
  secondLevelApprovalRemarks: Yup.mixed().nullable(),
  thirdLevelApprovalStatus: Yup.mixed().nullable(),
  thirdLevelApprovalBy: Yup.mixed().nullable(),
  thirdLevelApprovalDate: Yup.mixed().nullable(),
  thirdLevelApprovalRemarks: Yup.mixed().nullable(),
  sapTeamApprovalStatus: Yup.mixed().nullable(),
  sapTeamApprovalBy: Yup.mixed().nullable(),
  sapTeamApprovalDate: Yup.mixed().nullable(),
  sapTeamApprovalRemarks: Yup.mixed().nullable(),
  financeTeamApprovalStatus: Yup.mixed().nullable(),
  financeTeamApprovalBy: Yup.mixed().nullable(),
  financeTeamApprovalDate: Yup.mixed().nullable(),
  financeTeamApprovalRemarks: Yup.mixed().nullable(),

  //vendorAgreementStatus: Yup.string().oneOf(["Y", "N", ""]).required("Vendor Agreement Status is required"),
  vendorAgreementStatus: Yup.string()
  .oneOf(["Y", "N", ""], "Invalid status")
  .nullable(),
  

  //  vendorAgreementStatusFromDate: Yup.string()
  //   .transform((v, originalValue) =>
  //     originalValue === "" ? null : v,
  //   )
  //   .when("vendorAgreementStatus", {
  //     is: "Y",
  //     then: (schema) =>
  //       schema.required("Vendor Agreement Status From Date is required"),
  //     otherwise: (schema) => schema.nullable(),
  //   }),

  // vendorAgreementStatusToDate: Yup.string()
  //   // MUI `type="date"` empty state is `""`. Treat it as missing.
  //   .transform((v, originalValue) =>
  //     originalValue === "" ? null : v,
  //   )
  //   .when("vendorAgreementStatus", {
  //     is: "Y",
  //     then: (schema) =>
  //       schema.required("Vendor Agreement Status To Date is required"),
  //     otherwise: (schema) => schema.nullable(),
  //   }),

  vendorAgreementStatusFromDate: Yup.string()
  .nullable()
  .transform((v, originalValue) =>
    originalValue === "" ? null : v
  ),

vendorAgreementStatusToDate: Yup.string()
  .nullable()
  .transform((v, originalValue) =>
    originalValue === "" ? null : v
  ),

  //securityChequeDetails: Yup.string().trim().required("Security Cheque Details is required"),
  securityChequeDetails: Yup.string().trim().nullable(),

  isPanNoAlreadyExists: Yup.string().oneOf(["Y", "N"]).nullable(),
  isGstNoAlreadyExists: Yup.string().oneOf(["Y", "N"]).nullable(),
  isAccNoAlreadyExists: Yup.string().oneOf(["Y", "N"]).nullable(),
});

const VendorRegistrationForm = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [maxStepReached, setMaxStepReached] = useState(0);
  const { vendorRegistrationId: vendorRegistrationListId } = useParams();
  const isEditPage = Boolean(vendorRegistrationListId);
  const [loading, setLoading] = useState(!!vendorRegistrationListId);
  const stepperRef = useRef(null);
  const allowFinalSubmitRef = useRef(false);
  // Tracks the last successfully-submitted payload, so "Next" can skip API calls
  // when the user didn't change anything in edit mode.
  const lastSavedSignatureRef = useRef(null);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
  const [vendorRegistrationId, setVendorRegistrationId] = useState(
    isEditPage ? vendorRegistrationListId : null,
  );
  
  const [statutoryDuplicateDialog, setStatutoryDuplicateDialog] = useState({
    open: false,
    message: "",
  });
  const openStatutoryDuplicateDialog = useCallback((message) => {
    setStatutoryDuplicateDialog({
      open: true,
      message: String(message || ""),
    });
  }, []);
  const closeStatutoryDuplicateDialog = useCallback(() => {
    setStatutoryDuplicateDialog({ open: false, message: "" });
  }, []);

  const [ownerContactDuplicateDialog, setOwnerContactDuplicateDialog] =
    useState({
      open: false,
    });
  const openOwnerContactDuplicateDialog = useCallback(() => {
    setOwnerContactDuplicateDialog({ open: true });
  }, []);
  const closeOwnerContactDuplicateDialog = useCallback(() => {
    setOwnerContactDuplicateDialog({ open: false });
  }, []);

  const isAdmin = JSON.parse(sessionStorage.getItem("userData"))?.groupId === 1;


  const steps = isAdmin ? STEP_LABELS_ALL : STEP_LABELS_NO_OFFICE_USE;
  const termsStepIndex = isAdmin ? 4 : 4;
  const cobcStepIndex = isAdmin ? 5 : 5;
  const getFieldGroupIndex = (visibleStep) => {
    if (!isAdmin) {
      return visibleStep === 4 ? 5 : visibleStep;
    }
    // Admin visible order: Company, Factory, Business, Statutory, Terms, COBC, Review, Office
    if (visibleStep === 4) return 5; // Terms
    if (visibleStep === 5) return 6; // COBC
    if (visibleStep === 6) return 7; // Review
    if (visibleStep === 7) return 4; // Office Use
    return visibleStep; // 0..3
  };

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "instant" });
    }, 0);
  }, [activeStep]);

  useEffect(() => {
    getAllPaymentTerms(0, 9999)
      .then((res) => {
        const list = res?.response?.responseObject ?? [];
        setPaymentTermsOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setPaymentTermsOptions([]);
      });
  }, []);


  useEffect(() => {
    if (vendorRegistrationListId != null && vendorRegistrationListId !== "") {
      setLoading(true);
      getVendorRegistrationById(vendorRegistrationListId)
        .then((res) => {
          setLoading(false);
          const raw = res?.response?.responseObject ?? res?.response;
          const data = Array.isArray(raw) ? raw[0] : raw;
          const hasValidRecord =
            data &&
            typeof data === "object" &&
            (data.vendorRegistrationId != null || data.id != null);
          if (hasValidRecord) {
            setSelectedForEdit(data);
            const id = data.vendorRegistrationId ?? data.id;
            if (id != null) setVendorRegistrationId(id);
            const savedStep =
              data.currentStep ?? data.activeStep ?? data.current_step ?? 0;
            const stepNum = Math.min(
              Math.max(0, Number(savedStep)),
              steps.length - 1,
            );
            if (!Number.isNaN(stepNum)) {

              // start review from Company Details so each tab can be checked in order.
              // const initialStep = isAdmin && stepNum === 6 ? 0 : stepNum;
              setActiveStep(isAdmin? 0 : stepNum);
              setMaxStepReached(stepNum);
            }
          } else {
            setSelectedForEdit(null);
          }
        })
        .catch(() => {
          setLoading(false);
          setSelectedForEdit(null);
        });
    } else {
      setSelectedForEdit(null);
    }
  }, [vendorRegistrationListId]);

  const stepFieldGroups = [
    // Step 0 - Company Details tab
    [
      "companyName",
      "shareholdingStatus",
      "shareholdingStatusOtherRemarks",
      "natureOfBusiness",
      "companyStreetDoorNo",
      "companyAddressLine1",
      "companyAddressLine2",
      "companyAddressLine3",
      "companyAddressLine4",
      "companyAddressLine5",
      "companyPostalCode",
      "companyCity",
      "companyStateRegion",
      "companyCountry",
      "officeAddress",
      "vendorTypeId",
      "vendorTypeCode",
      "vendorTypeSubClassification",
      "yearOfEstablishment",
      "sendInvite",
      "ownerName",
      "ownerFathersName",
      "ownerContactNo",
      "isOwnerContactNoAlreadyExists",
      "ownerEmail",
      "partnerName",
      "partnerContactNo",
      "partnerEmail",
      "primaryContactName",
      "primaryContactDesignation",
      "primaryContactNo",
      "primaryContactEmail",
      "secondaryContactName",
      "secondaryContactDesignation",
      "secondaryContactNo",
      "secondaryContactEmail",
      "secondaryContactName2",
      "secondaryContactDesignation2",
      "secondaryContactNo2",
      "secondaryContactEmail2",
      "unitInspectedBy",
    ],
    // Step 1 - Factory Information tab (UI: Step3FactoryInfo)
    [
      "factoryStreetDoorNo",
      "factoryAddressLine1",
      "factoryAddressLine2",
      "factoryAddressLine3",
      "factoryAddressLine4",
      "factoryAddressLine5",
      "factoryPostalCode",
      "factoryCity",
      "factoryStateRegion",
      "factoryCountry",
      "factoryAddressSameAsCompany",
      "factoryAddress",
      "factoryLicenceNo",
      "noOfPlants",
      "totalAreaSqft",
      "totalProductionCapacityPerShift",
      "totalProductionCapacityPerMonth",
      "numberOfEmployeesLabours",
      "ownRentalDetails",
    ],
    // Step 2 - Business Details tab (UI: Step2BusinessDetails)
    [
      "turnoverApplicable",
      "turnoverYear1",
      "turnoverRevenue1",
      "turnoverYear2",
      "turnoverRevenue2",
      "turnoverYear3",
      "turnoverRevenue3",
      "natureProductRange1",
      "natureBusinessShare1",
      "natureProductRange2",
      "natureBusinessShare2",
      "natureProductRange3",
      "natureBusinessShare3",
      "natureProductRange4",
      "natureBusinessShare4",
      "natureProductRange5",
      "natureBusinessShare5",
      "top5Customer1",
      "top5Customer2",
      "top5Customer3",
      "top5Customer4",
      "top5Customer5",
    ],
    // Step 3 - Statutory Details tab
    [
      "itrAcknowledgementNo1",
      "itrDateOfFiling1",
      "itrAcknowledgementNo2",
      "itrDateOfFiling2",
      "gstApplicable",
      "statutoryGstNo",
      "statutoryPanNo",
      "isPanNoAlreadyExists",
      "isGstNoAlreadyExists",
      "isAccNoAlreadyExists",
      "aadharLinkWithPan",
      "msmeDeclaration",
      "msmeType",
      "msmeNo",
      "esiNo",
      "pfNo",
      "statutoryBankName",
      "branch",
      "statutoryIfscCode",
      "statutoryAccountNumber",
      "paymentTerms",
      "gstCertificateAttachment",
      "gstSelfDeclarationAttachment",
      "panScanAttachment",
      "aadharPanLinkAttachment",
      "aadharPanNoSignatureAttachment",
      "msmeCertificateAttachment",
      "msmeNoSignatureAttachment",
      "bankDocumentAttachment",
      "msmeValidityFromDate",
      "msmeValidityToDate",
      "vendorAgreementStatus",
      "vendorAgreementStatusFromDate",
      "vendorAgreementStatusToDate",
      "securityChequeDetails"
    ],
    //step 5 - office use
    [
      "withholdingTaxType",
      "withholdingTaxCode",
      "subject",
      "qsrec",
      "vendorCode",
      "vendorCategory",
      "reconGroup",
      "subReconGroup",
      "brand",
      "approvalStatus",
      "approvalRemarks",
      "firstLevelApprovalStatus",
      "firstLevelApprovalBy",
      "firstLevelApprovalDate",
      "firstLevelApprovalRemarks",
      "secondLevelApprovalStatus",
      "secondLevelApprovalBy",
      "secondLevelApprovalDate",
      "secondLevelApprovalRemarks",
      "thirdLevelApprovalStatus",
      "thirdLevelApprovalBy",
      "thirdLevelApprovalDate",
      "thirdLevelApprovalRemarks",
      "sapTeamApprovalStatus",
      "sapTeamApprovalBy",
      "sapTeamApprovalDate",
      "sapTeamApprovalRemarks",
      "financeTeamApprovalStatus",
      "financeTeamApprovalBy",
      "financeTeamApprovalDate",
      "financeTeamApprovalRemarks",
    ],
    [
      "termsAgreedDateTime",
    ],
    [
      "cobcAgreedDateTime",
    ],
    // Step 4 - Review & Submit (no form fields)
    ["overallComments", "agreeTerms","isEdit"],
  ];

  const stableStringify = (value) => {
    if (value === null || value === undefined) return String(value);
    if (value instanceof Date) return value.toISOString();
    if (typeof File !== "undefined" && value instanceof File) return "__FILE__";
    if (typeof value !== "object") return JSON.stringify(value);
    if (Array.isArray(value)) {
      return `[${value.map((v) => stableStringify(v)).join(",")}]`;
    }
    const keys = Object.keys(value).sort();
    return `{${keys
      .map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
      .join(",")}}`;
  };

  const getPayloadSignature = (formValues) => {
    // Signature must match what we send to backend (except vendorRegistrationId/currentStep),
    // but should avoid attachment File objects to keep comparisons stable.
    const stepsToInclude = [];
    for (let s = 0; s <= 5; s++) {
      const fields = stepFieldGroups[s] || [];
      stepsToInclude.push(...fields);
    }

    const payload = {};
    stepsToInclude.forEach((key) => {
      const lowerKey = String(key).toLowerCase();
      if (lowerKey.includes("attachment")) return;

      const val = formValues?.[key];
      if (val === undefined || val === null) return;
      if (typeof File !== "undefined" && val instanceof File) return;
      payload[key] = val;
    });

    // Combine multiline address fields into legacy single-line fields (same logic as handleNext)
    if (formValues?.companyAddressLine1 || formValues?.companyStreetDoorNo) {
      const parts = [
        formValues.companyStreetDoorNo,
        formValues.companyAddressLine1,
        formValues.companyAddressLine2,
        formValues.companyAddressLine3,
        formValues.companyAddressLine4,
        formValues.companyAddressLine5,
        formValues.companyCity,
        formValues.companyStateRegion,
        formValues.companyPostalCode,
        formValues.companyCountry,
      ]
        .filter((p) => p != null && String(p).trim() !== "")
        .map((p) => String(p).trim());
      payload.officeAddress = parts.join(", ");
    }

    if (formValues?.factoryAddressLine1 || formValues?.factoryStreetDoorNo) {
      const parts = [
        formValues.factoryStreetDoorNo,
        formValues.factoryAddressLine1,
        formValues.factoryAddressLine2,
        formValues.factoryAddressLine3,
        formValues.factoryAddressLine4,
        formValues.factoryAddressLine5,
        formValues.factoryCity,
        formValues.factoryStateRegion,
        formValues.factoryPostalCode,
        formValues.factoryCountry,
      ]
        .filter((p) => p != null && String(p).trim() !== "")
        .map((p) => String(p).trim());
      payload.factoryAddress = parts.join(", ");
    }

    return stableStringify(payload);
  };

  useEffect(() => {
    // When editing existing record, initialize the "last saved" snapshot from loaded form values.
    if (selectedForEdit) {
      const initialFormValues = mapApiResponseToFormValues(
        selectedForEdit,
        initialValues,
      );
      lastSavedSignatureRef.current = getPayloadSignature(initialFormValues);
    } else {
      lastSavedSignatureRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedForEdit]);

  const handleSubmitApi = async (values, resetForm) => {
    if (!allowFinalSubmitRef.current || activeStep !== steps.length - 1) {
      allowFinalSubmitRef.current = false;
      return;
    }
    allowFinalSubmitRef.current = false;
    setLoading(true);

    // Log all form data when Submit is clicked (check if all data is saved)
    console.log("All form values:", values);
    // Summary: which fields have values (useful for file/image fields)
    const filledKeys = Object.keys(values).filter(
      (k) => values[k] !== null && values[k] !== undefined && values[k] !== "",
    );
    console.log("Fields with values (" + filledKeys.length + "):", filledKeys);
    // Image/file fields summary (File objects don't stringify well)

    try {
      const payload = { ...values };
      if (vendorRegistrationId) {
        payload.vendorRegistrationId = vendorRegistrationId;
      }

      payload.currentStep = steps.length - 1;
      payload.isEdit= values.isEdit;
      payload.regNo= values.regNo;
      payload.preparedBy= values.preparedBy;
      console.log("Final payload:", payload);

      const res = await saveOrUpdateVendorRegistration(payload);

      console.log("API Response:", res);

      toast.success("Vendor registration saved successfully!");

      const isVendorUser =
        JSON.parse(sessionStorage.getItem("userData")).groupId === 0;
      const isAdminUser =
        JSON.parse(sessionStorage.getItem("userData")).groupId === 1;
      const hasVendorCode =
        isVendorUser &&
        JSON.parse(sessionStorage.getItem("userData"))?.hasVendorCode === "Y";
      if (isVendorUser && !hasVendorCode) {
        setActiveStep(0);
        navigate(
          `${process.env.PUBLIC_URL}/master/vendorRegistrationForm/${JSON.parse(sessionStorage.getItem("userData")).userId}`,
        );
      } else {
        if (isAdminUser) {
          resetForm();
          // redirect to list page
          navigate(`${process.env.PUBLIC_URL}/master/vendorRegistrationList`);
        } else {
          navigate(`${process.env.PUBLIC_URL}/dashboard`);
        }
      }
    } catch (error) {
      toast.error("Error submitting form. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const noDataFound =
    isEditPage && !loading && selectedForEdit === null && vendorRegistrationListId;

  if (noDataFound) {
    return (
      <Fragment>
        <Breadcrumb
          title="Vendor Registraion Form"
          parent={
            isAdmin && (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  color="secondary"
                  variant="contained"
                  type="button"
                  startIcon={<ArrowBack />}
                  onClick={() =>
                    navigate(
                      `${process.env.PUBLIC_URL}/master/vendorRegistrationList`,
                    )
                  }
                  size="small"
                >
                  Back to List
                </Button>
              </div>
            )
          }
        />
        <Container fluid={true}>
          <Card className={classes.card}>
            <CardContent>
              <Typography
                variant="h6"
                color="textSecondary"
                align="center"
                sx={{ py: 4 }}
              >
                No Vendor Registration Details found
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {loading && <CommonLoader />}
      <Breadcrumb
        title="Vendor Registration Form"
        parent={
          //onCancel ? (
          isAdmin && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                //variant="outlined"
                color={"secondary"}
                variant="contained"
                type="button"
                startIcon={<ArrowBack />}
                onClick={() => navigate(-1)}
                size="small"
              >
                Back to List
              </Button>
            </div>
          )
          // ) : null
        }
      />
      <Box ref={stepperRef} className={classes.stickyStepper}>
        <Stepper activeStep={activeStep} alternativeLabel nonLinear>
          {steps.map((label, index) => (
            <Step key={label} disabled={false}>
              {maxStepReached >= 2 && index <= maxStepReached ? (
                <StepButton onClick={() => setActiveStep(index)}>
                  {label}
                </StepButton>
              ) : (
                <StepLabel>{label}</StepLabel>
              )}
            </Step>
          ))}
        </Stepper>
      </Box>
      <Container fluid={true}>
        <Card className={classes.card}>
          <CardContent>
            <div className={classes.root}>
              <Formik
                key={selectedForEdit?.vendorRegistrationId ?? "new"}
                initialValues={
                  selectedForEdit
                    ? mapApiResponseToFormValues(selectedForEdit, initialValues)
                    : initialValues
                }
                validationSchema={validationSchema}
                enableReinitialize
                validateOnChange={true}
                validateOnBlur={true}
                onSubmit={(values, { resetForm }) => {
                  handleSubmitApi(values, resetForm);
                }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  setFieldValue,
                  validateForm,
                  setTouched,
                  resetForm,
                  handleBlur,
                }) => {

                 const isVendorUser =
                    JSON.parse(sessionStorage.getItem("userData"))?.groupId === 0;

                  const disableForEdit =
                    isVendorUser && values.isEdit === "N";

                  const originalOwnerContact =
                    selectedForEdit?.ownerContactNo ??
                    selectedForEdit?.owner_contact_no ??
                    "";

                  const originalStatutory = {
                    pan:
                      selectedForEdit?.statutoryPanNo ??
                      selectedForEdit?.statutorypan_no ??
                      "",
                    gst:
                      selectedForEdit?.statutoryGstNo ??
                      selectedForEdit?.statutorygst_no ??
                      "",
                    account:
                      selectedForEdit?.statutoryAccountNumber ??
                      selectedForEdit?.statutoryaccount_number ??
                      "",
                  };
                  const originalDuplicateFlags = {
                    isPanNoAlreadyExists:
                      selectedForEdit?.isPanNoAlreadyExists ??
                      selectedForEdit?.is_pan_no_already_exists ??
                      "N",
                    isGstNoAlreadyExists:
                      selectedForEdit?.isGstNoAlreadyExists ??
                      selectedForEdit?.is_gst_no_already_exists ??
                      "N",
                    isAccNoAlreadyExists:
                      selectedForEdit?.isAccNoAlreadyExists ??
                      selectedForEdit?.is_acc_no_already_exists ??
                      "N",
                  };

                  const handleOwnerContactBlur = (e) => {
                    handleBlur(e);
                    const raw = values.ownerContactNo;
                    if (raw == null || String(raw).trim() === "") {
                      setFieldValue("isOwnerContactNoAlreadyExists", "N");
                      return;
                    }
                    const trimmed = String(raw).trim();
                    if (!/^\d{10}$/.test(trimmed)) {
                      return;
                    }
                    const originalTrimmed =
                      originalOwnerContact != null
                        ? String(originalOwnerContact).trim()
                        : "";
                    if (
                      vendorRegistrationId != null &&
                      vendorRegistrationId !== "" &&
                      originalTrimmed !== "" &&
                      trimmed === originalTrimmed
                    ) {
                      return;
                    }
                    checkVendorRegistrationStatutoryDuplicate(
                      "OWNER_CONTACT",
                      trimmed,
                      vendorRegistrationId,
                    )
                      .then((res) => {
                        if (res.exists) {
                          setFieldValue("isOwnerContactNoAlreadyExists", "Y");
                          openOwnerContactDuplicateDialog();
                        } else {
                          setFieldValue("isOwnerContactNoAlreadyExists", "N");
                        }
                      })
                      .catch(() =>
                        setFieldValue("isOwnerContactNoAlreadyExists", "N"),
                      );
                  };

                  /** Blur duplicate check for PAN, GST, and bank account (single API). */
                  const handleStatutoryDuplicateBlur = (e) => {
                    handleBlur(e);
                    const name = e.target.name;
                    if (
                      name !== "statutoryPanNo" &&
                      name !== "statutoryGstNo" &&
                      name !== "statutoryAccountNumber"
                    ) {
                      return;
                    }
                    if (name === "statutoryGstNo" && values.gstApplicable !== "Y") {
                      return;
                    }
                    const FIELD_META = {
                      statutoryPanNo: {
                        type: "PAN",
                        flag: "isPanNoAlreadyExists",
                        message:
                          "This PAN number is already registered for another vendor.",
                      },
                      statutoryGstNo: {
                        type: "GST",
                        flag: "isGstNoAlreadyExists",
                        message:
                          "This GST number is already registered for another vendor.",
                      },
                      statutoryAccountNumber: {
                        type: "ACCOUNT",
                        flag: "isAccNoAlreadyExists",
                        message:
                          "This account number is already registered for another vendor.",
                      },
                    };
                    const meta = FIELD_META[name];
                    const raw = values[name];
                    if (raw == null || String(raw).trim() === "") {
                      setFieldValue(meta.flag, "N");
                      return;
                    }
                    const trimmed = String(raw).trim();
                    const originalRaw =
                      name === "statutoryPanNo"
                        ? originalStatutory.pan
                        : name === "statutoryGstNo"
                          ? originalStatutory.gst
                          : originalStatutory.account;
                    const originalTrimmed =
                      originalRaw != null ? String(originalRaw).trim() : "";
                    if (
                      vendorRegistrationId != null &&
                      vendorRegistrationId !== "" &&
                      originalTrimmed !== "" &&
                      trimmed === originalTrimmed
                    ) {
                      const prevFlag =
                        String(originalDuplicateFlags[meta.flag] ?? "N")
                          .toUpperCase() === "Y"
                          ? "Y"
                          : "N";
                      setFieldValue(meta.flag, prevFlag);
                      return;
                    }
                    checkVendorRegistrationStatutoryDuplicate(
                      meta.type,
                      trimmed,
                      vendorRegistrationId,
                    )
                      .then((res) => {
                        if (res.exists) {
                          setFieldValue(meta.flag, "Y");
                          openStatutoryDuplicateDialog(meta.message);
                        } else {
                          setFieldValue(meta.flag, "N");
                        }
                      })
                      .catch(() => setFieldValue(meta.flag, "N"));
                  };
                  const handleNext = async (advance = true) => {
                    const groupIndex = getFieldGroupIndex(activeStep);
                    const stepFields = stepFieldGroups[groupIndex] || [];
                    // Validate only current step so async statutory duplicate checks (Step 4) do not run on other steps
                    let allErrors = {};
                    if (stepFields.length > 0) {
                      const stepSchema = validationSchema.pick(stepFields);
                      try {
                        await stepSchema.validate(values, {
                          abortEarly: false,
                        });
                      } catch (err) {
                        if (err.inner) {
                          err.inner.forEach((e) => {
                            if (e.path) allErrors[e.path] = e.message;
                          });
                        }
                      }
                    }
                    const stepErrorsKeys = Object.keys(allErrors);

                    if (
                      groupIndex === 0 &&
                      values.isOwnerContactNoAlreadyExists === "Y"
                    ) {
                      toast.error(
                        "This owner contact number is already registered for another vendor.",
                      );
                      setTouched({ ...touched, ownerContactNo: true });
                      return;
                    }

                    if (stepErrorsKeys.length > 0) {
                      const newTouched = { ...touched };
                      stepErrorsKeys.forEach((key) => {
                        newTouched[key] = true;
                      });
                      setTouched(newTouched);
                      //FOR HIDDEN ERROR FINDING
                      // const firstErrorKey = stepErrorsKeys[0];
                      // const firstErrorMessage = allErrors[firstErrorKey];
                      // if (firstErrorKey) {
                      //   const fieldEl = document.querySelector(
                      //     `[name="${firstErrorKey}"]`,
                      //   );
                      //   fieldEl?.scrollIntoView({
                      //     behavior: "smooth",
                      //     block: "center",
                      //   });
                      //   fieldEl?.focus?.();
                      // }
                      toast.error(
                       "Please correct errors in this step before continuing.",
                      );
                      return;
                    }

                    // setLoading(true);
                    try {
                      let didCallVendorRegistrationApi = false;
                      // Steps that should NOT call saveOrUpdateVendorRegistration:
                      // - Terms & Conditions (Step6): activeStep=5 for admin, activeStep=4 for non-admin
                      // - COB (Step7): activeStep=6 for admin
                      // const isTermsStep =
                      //   (!isAdmin && activeStep === 4) ||
                      //   (isAdmin && activeStep === 5);
                      // const isCobStep = isAdmin
                      //   ? activeStep === 6
                      //   : activeStep === 5;

                      const isTermsStep = activeStep === termsStepIndex;
                      const isCobStep = activeStep === cobcStepIndex;

                      // Steps 0–4 (data entry tabs) -> save to Vendor Registration table
              
                      // Admin includes Office Use as last tab now.
                      const lastDataEntryStep = isAdmin ? 7 : 4;

                      if (
                        activeStep >= 0 &&
                        activeStep <= lastDataEntryStep &&
                        !isTermsStep &&
                        !isCobStep
                      ) {
                        // Always include ALL steps 0–4 in payload so backend is never sent partial data.
                        const stepsToInclude = [];
                        for (let s = 0; s <= 5; s++) {
                          const fields = stepFieldGroups[s] || [];
                          stepsToInclude.push(...fields);
                        }
                        const payload = {};
                        stepsToInclude.forEach((key) => {
                          const val = values[key];
                          if (val !== undefined && val !== null) {
                            payload[key] = val;
                          }
                        });
                        // Combine multiline address fields into legacy single-line fields
                        if (
                          values.companyAddressLine1 ||
                          values.companyStreetDoorNo
                        ) {
                          const parts = [
                            values.companyStreetDoorNo,
                            values.companyAddressLine1,
                            values.companyAddressLine2,
                            values.companyAddressLine3,
                            values.companyAddressLine4,
                            values.companyAddressLine5,
                            values.companyCity,
                            values.companyStateRegion,
                            values.companyPostalCode,
                            values.companyCountry,
                          ]
                            .filter((p) => p != null && String(p).trim() !== "")
                            .map((p) => String(p).trim());
                          payload.officeAddress = parts.join(", ");
                        }
                        if (
                          values.factoryAddressLine1 ||
                          values.factoryStreetDoorNo
                        ) {
                          const parts = [
                            values.factoryStreetDoorNo,
                            values.factoryAddressLine1,
                            values.factoryAddressLine2,
                            values.factoryAddressLine3,
                            values.factoryAddressLine4,
                            values.factoryAddressLine5,
                            values.factoryCity,
                            values.factoryStateRegion,
                            values.factoryPostalCode,
                            values.factoryCountry,
                          ]
                            .filter((p) => p != null && String(p).trim() !== "")
                            .map((p) => String(p).trim());
                          payload.factoryAddress = parts.join(", ");
                        }

                        const isEditMode =
                          vendorRegistrationId != null &&
                          vendorRegistrationId !== "";
                        // Include step index so moving to a new tab still updates currentStep
                        // even if form values did not change.
                        const currentSignature = `${activeStep}::${getPayloadSignature(values)}`;
                        const shouldCallVendorRegistrationApi =
                          !isEditMode ||
                          lastSavedSignatureRef.current == null ||
                          currentSignature !== lastSavedSignatureRef.current;

                        if (shouldCallVendorRegistrationApi) {
                          if (isEditMode) {
                            payload.vendorRegistrationId = vendorRegistrationId;
                          }
                          // Persist current visible tab index (0-based), not next index.
                          payload.currentStep = activeStep;
                          payload.isEdit= values.isEdit;
                          payload.regNo= values.regNo;
                          payload.preparedBy= values.preparedBy;
                          const res =
                            await saveOrUpdateVendorRegistration(payload);
                          console.log(res, "res");
                          const data =
                            res?.response?.responseObject ??
                            res?.response ??
                            {};
                          const id =
                            data.id ??
                            data.vendorRegistrationId ??
                            data.vendor_registration_id;
                          if (id != null && id !== "") {
                            setVendorRegistrationId(id);
                          }

                          lastSavedSignatureRef.current = currentSignature;
                          didCallVendorRegistrationApi = true;
                        }
                      }
                      // Step 3 (Annexure Digital Images / Attachments) -> save to Vendor Evaluation File Details table
                      if (activeStep === 3) {
                        const formData = new FormData();
                        if (
                          vendorRegistrationId != null &&
                          vendorRegistrationId !== ""
                        ) {
                          formData.append(
                            "vendorRegistrationId",
                            vendorRegistrationId,
                          );
                        }
                        const fileFields = stepFieldGroups[3] || [];
                        fileFields.forEach((key) => {
                          const file = values[key];
                          if (
                            file &&
                            (file instanceof File ||
                              (file.name && file.size !== undefined))
                          ) {
                            formData.append(key, file);
                          }
                        });
                        // await saveOrUpdateVendorEvaluationFileDetails(formData);
                      }

                      if (advance) {
                        const nextStep = activeStep + 1;
                        setActiveStep(nextStep);
                        setMaxStepReached((prev) => Math.max(prev, nextStep));

                        window.scrollTo({ top: 0, behavior: "instant" });
                        if (stepperRef.current) {
                          stepperRef.current.scrollIntoView({
                            behavior: "instant",
                            block: "start",
                          });
                        }
                      }
                      if (didCallVendorRegistrationApi || !advance) {
                        if (activeStep === 3) {
                          toast.success("Attachments saved successfully.");
                        } else if (
                          isAdmin &&
                          activeStep === steps.length - 1
                        ) {
                          toast.success("Office use details saved successfully.");
                        } else {
                          toast.success("Details saved successfully.");
                        }
                      }
                    } catch (err) {
                      toast.error(
                        err?.response?.data?.message ||
                          "Failed to save. Please try again.",
                      );
                    } finally {
                      setLoading(false);
                    }
                  };


                  const handleBack = () => {
                    setActiveStep((prev) => prev - 1);
                    // Scroll to top immediately
                    window.scrollTo({ top: 0, behavior: "instant" });
                    if (stepperRef.current) {
                      stepperRef.current.scrollIntoView({
                        behavior: "instant",
                        block: "start",
                      });
                    }
                  };

                  const handleReset = () => {
                    resetForm();
                    setActiveStep(0);
                    setVendorRegistrationId(null);
                  };

                  const getStepContent = (step) => {
                    switch (step) {
                      case 0:
                        return (
                          <Step1CompanyDetails
                            formData={values}
                            onFieldChange={handleChange}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                            onOwnerContactBlur={handleOwnerContactBlur}
                            groupId={{
                              isAdmin,
                            }}
                            isEdit={isEditPage}
                            isVendorUser={isVendorUser}

                          />
                        );
                      case 1:
                        return (
                          <Step3FactoryInfo
                            formData={values}
                            onFieldChange={handleChange}
                            setFieldValue={setFieldValue}
                            setFormData={(updater) => {
                              const next =
                                typeof updater === "function"
                                  ? updater(values)
                                  : updater;
                              Object.keys(next).forEach((key) =>
                                setFieldValue(key, next[key]),
                              );
                            }}
                             errors={errors}
                            touched={touched}
                          />
                        );
                      case 2:
                        return (
                          <Step2BusinessDetails
                            formData={values}
                            onFieldChange={handleChange}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                          />
                        );
                      case 3:
                        return (
                          <Step4Statutory
                            formData={values}
                            setFieldValue={setFieldValue}
                            onStatutoryDuplicateBlur={handleStatutoryDuplicateBlur}
                            errors={errors}
                            touched={touched}
                            paymentTermsOptions={paymentTermsOptions}
                            isVendorUser={isVendorUser}
                          />
                        );
                    
                      case 4:
                       return (
                            <Step6Terms
                              formData={values}
                              setFieldValue={setFieldValue}
                              vendorRegistrationId={vendorRegistrationId}
                                groupId={{
                              isAdmin,
                            }}
                            />
                        
                          );
                      case 5:
                        return (
                          <Step7COBC
                            formData={values}
                            setFieldValue={setFieldValue}
                            vendorRegistrationId={vendorRegistrationId}
                             groupId={{
                              isAdmin,
                            }}
                          />
                        );

                      case 6:
                        return (
                          <Step8Review
                            formData={values}
                            setFieldValue={setFieldValue}
                            vendorRegistrationId={vendorRegistrationId}
                            groupId={{
                              isAdmin,
                            }}
                            onRedirectToStep0={() => setActiveStep(0)}
                          />
                        );
                      case 7:
                        return isAdmin ? (
                          <Step5OfficeUse
                            formData={values}
                            setFieldValue={setFieldValue}
                          />
                        ) : null;
                      default:
                        return "Unknown step";
                    }
                  };

                  const handleSubmit = (e) => {
                    // Intentionally left blank: submit is handled via explicit button click
                  };

                  const handleSendInvite = async () => {
                    // Validate only the invite-related fields
                    const inviteFields = [
                      "companyName",
                      "ownerName",
                      "ownerContactNo",
                      "ownerEmail",
                    ];
                    const inviteSchema = validationSchema.pick(inviteFields);

                    let inviteErrors = {};
                    try {
                      await inviteSchema.validate(values, {
                        abortEarly: false,
                      });
                    } catch (err) {
                      if (err.inner) {
                        err.inner.forEach((e) => {
                          if (e.path) inviteErrors[e.path] = e.message;
                        });
                      }
                    }

                    const errorKeys = Object.keys(inviteErrors);
                    if (errorKeys.length > 0) {
                      const newTouched = { ...touched };
                      errorKeys.forEach((key) => {
                        newTouched[key] = true;
                      });
                      setTouched(newTouched);
                      toast.error(
                        "Please fill Company Name, Owner Name, Contact No and E-mail before sending invite.",
                      );
                      return;
                    }

                    try {
                      setLoading(true);
                      const payload = {
                        companyName: values.companyName,
                        ownerName: values.ownerName,
                        ownerContactNo: values.ownerContactNo,
                        ownerEmail: values.ownerEmail,
                      };
                      await vendorRegistrationSendInvite(payload);
                      toast.success("Invite sent successfully.");

                      resetForm();

                      setTimeout(() => {
                        navigate(
                          `${process.env.PUBLIC_URL}/master/vendorRegistrationList`,
                        );
                      }, 1200);
                    } catch (err) {
                      toast.error(
                        err?.response?.data?.message ||
                          "Failed to send invite. Please try again.",
                      );
                    } finally {
                      setLoading(false);
                    }
                  };

                  return (
                    <Form>
                      <div className={classes.stepContent}>
                        {activeStep === steps.length ? (
                          <Box>
                            <Typography>All steps completed</Typography>
                            <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
                              Reset
                            </Button>
                          </Box>
                        ) : (
                          <Box>
                            {getStepContent(activeStep)}
                            <Box className={classes.buttonContainer}>
                              {activeStep > 0 && (
                                <Button
                                  onClick={handleBack}
                                  sx={{ mr: 1 }}
                                  type="button"
                                >
                                  Back
                                </Button>
                              )}
                              {/* <Box>
                                {activeStep === steps.length - 1 ? (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    onClick={() => {
                                      allowFinalSubmitRef.current = true;
                                    }}
                                    disabled={
                                      loading ||
                                      (!isAdmin &&
                                        !(
                                          values.termsAgreedDateTime != null &&
                                          String(values.termsAgreedDateTime).trim() !== "" &&
                                          values.cobcAgreedDateTime != null &&
                                          String(values.cobcAgreedDateTime).trim() !== ""
                                        ))
                                    }
                                  >
                                    {loading ? "Submitting..." : "Submit"}
                                  </Button>
                                ) : (
                                  <>
                                    {activeStep === 0 &&
                                    !isEditPage &&
                                    values.sendInvite === true &&
                                    isAdmin ? (
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSendInvite}
                                        type="button"
                                        disabled={loading}
                                        sx={{ mr: 1 }}
                                      >
                                        Send Invite
                                      </Button>
                                    ) : (
                                      values.sendInvite !== true && (
                                        <Button
                                          variant="contained"
                                          color="primary"
                                          onClick={handleNext}
                                          type="button"
                                       disabled={
                                          !isAdmin &&
                                          (loading ||
                                            (activeStep === termsStepIndex &&
                                              !Boolean(values.termsAccepted)) ||
                                            (activeStep === cobcStepIndex &&
                                              !Boolean(values.cobcAccepted)))
                                        }
                                        >
                                          Next
                                        </Button>
                                      )
                                    )}
                                  </>
                                )}
                              </Box> */}
                              <Box>
                                {activeStep !== steps.length - 1 ? (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleNext(true)}
                                    type="button"
                                    disabled={
                                      disableForEdit ||
                                      (!isAdmin &&
                                        (loading ||
                                          (activeStep === termsStepIndex &&
                                            !Boolean(values.termsAccepted)) ||
                                          (activeStep === cobcStepIndex &&
                                            !Boolean(values.cobcAccepted))))
                                    }
                                  >
                                    Next
                                  </Button>
                                ) : (
                                  isAdmin && (
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      onClick={() => handleNext(false)}
                                      type="button"
                                      disabled={loading || disableForEdit}
                                    >
                                      {loading ? "Saving..." : "Save"}
                                    </Button>
                                  )
                                )}
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </CardContent>
        </Card>
      </Container>
      <ToastContainer autoClose={2000} />
      <Dialog
        open={statutoryDuplicateDialog.open}
        onClose={closeStatutoryDuplicateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Duplicate statutory / bank detail</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {statutoryDuplicateDialog.message}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
            You can continue and save; the record will be flagged as having a
            duplicate PAN, GST, or account number where applicable.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatutoryDuplicateDialog} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={ownerContactDuplicateDialog.open}
        onClose={closeOwnerContactDuplicateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Duplicate Owner Contact Number</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This owner contact number is already registered for another vendor.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
            Please enter a different owner contact number to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOwnerContactDuplicateDialog} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default VendorRegistrationForm;
